export const botkeeperMeta = [
  { label: 'Year', value: '2025' },
  { label: 'Role', value: 'Senior Product Designer — Visual & Behavioral Design' },
  { label: 'Industry', value: 'AI accounting services' },
  { label: 'Context', value: 'Mid-platform redesign, phased rollout' },
];

export const botkeeperTools = ['Figma', 'Wynde', 'Storybook', 'Userflow', 'GA4'];

export const botkeeperImages = {
  oldNew: {
    src: 'https://cdn.magicpatterns.com/uploads/1R3GG83WLx9mg6BAtTYL4v/image.png',
    alt: 'Old versus new Client & User Management on two tablets: the old page has a long side nav and a Details tab of read-only forms; the new one has a compact rail, a client header and an editable users table',
  },
  hero: {
    src: 'https://cdn.magicpatterns.com/uploads/qytY3J89dLXM9VmXriggg4/image.png',
    alt: 'Botkeeper Client & User Management, old and new, on two tablets',
  },
  oldDetails: {
    src: 'https://cdn.magicpatterns.com/uploads/wDWWr53nFkWkAfjEThKReW/pasted-image.jpg',
    alt: 'The old Details tab: read-only Client Details, Configuration and Accounting Software panels, each behind a small Edit link',
  },
  notes: {
    src: 'https://cdn.magicpatterns.com/uploads/c5JL6dZ3zhzdbAJ7xPgg2t/Frame_14594.png',
    alt: 'Research synthesis: partner and client settings layouts annotated with requirements such as multi-select, bulk access changes and deactivation hand-off, beside an early clients table',
  },
  heatmap: {
    src: 'https://cdn.magicpatterns.com/uploads/35QpLm8S1AqTWQjrrsCgmp/pasted-image.png',
    alt: 'Attention Insight heat-map of the Settings page: attention concentrates on the partners table rather than the navigation',
  },
  newUsers: {
    src: 'https://cdn.magicpatterns.com/uploads/fRKieqZbgrrymWjRto9Dob/image.png',
    alt: 'The new Client & User Management page: a client header with editable accounting fields, and a Users table with row quick actions, status and 2FA columns, and Columns, Filters, Sort, Density and Export tools',
  },
};

export const botkeeperAttentionStudy =
  'https://cdn.magicpatterns.com/uploads/nfVAqZbrfQ8S7qc2x4KX2J/a3b152_a13beddf35bf40e9b7065e1d613e31c1.pdf.pdf';

export const botkeeperPainPoints = [
  {
    title: 'Broken navigation model',
    body: 'Stakeholders disagreed on the entry point. Users received conflicting instructions depending on who they asked.',
  },
  {
    title: 'Hidden critical action',
    body: 'Editing took 7 clicks and 58 seconds on average. Power users asked for in-row editing but had no path to it.',
  },
  {
    title: 'Permission assignment complexity',
    body: 'A 3-tab permission flow with no role-based defaults meant configuring access by hand every time, raising errors and cognitive load.',
  },
  {
    title: 'No bulk operations',
    body: 'Firms managing hundreds of client accounts had no way to act at scale. Every change was one by one.',
  },
];

export const botkeeperMethods = [
  {
    stat: '5 | 2',
    title: 'User shadowing',
    body: '5 power-user sessions and 2 onboarding sessions. An average Edit → Save took 7 clicks and 58 seconds.',
  },
  {
    stat: '280',
    title: 'Support tickets mined',
    body: '280 chat transcripts plus 57 “How do I edit…?” tickets. 72% of all issues traced back to the hidden Edit action.',
  },
  {
    stat: '1:1s',
    title: 'Stakeholder interviews',
    body: 'Misaligned mental models surfaced immediately: each team pointed users to a different starting page.',
  },
  {
    stat: '10',
    title: 'Heuristic audit',
    body: 'Nielsen’s 10 heuristics plus a WCAG quick scan: low visual hierarchy, redundant labels, no role-based defaults.',
  },
];

export const botkeeperDecisions = [
  {
    title: 'Side navigation over top tabs',
    body: 'Top tabs (Details | Users | Service Team) flattened a three-tier hierarchy. A side nav with persistent context — firm, client and a way back — kept users oriented, and scaled as the platform added modules.',
    before: 'Top tabs',
    after: 'Side nav',
  },
  {
    title: 'Gear icon for settings, not a buried modal',
    body: 'In the Attention Insight study the gear kept eyes on the data rows, where tabs and section headers pulled attention away. A task test (n = 12) confirmed it: 18 seconds faster, zero misclicks. A tooltip helps new users find it.',
    before: 'Hidden modal',
    after: 'Inline gear',
  },
  {
    title: '1-page permission wizard, pre-filled by role',
    body: 'One page replaced the 3-tab flow, with defaults set by role. Customer success managers (n = 5) wanted confirmation, so saving shows a toast with undo, tracked with a GA4 event.',
    before: '3-tab flow',
    after: '1-page wizard',
  },
  {
    title: 'Bulk actions as a first-class feature',
    body: 'For firms managing 100+ client accounts, one-by-one changes were a dealbreaker. Bulk select, role assignment and status changes were scoped from power-user feedback, and quick actions put the most common tasks in each row.',
    before: 'One by one',
    after: 'Bulk + quick actions',
  },
];

export const botkeeperShipping = [
  'Kept two Figma file states — current phase and future phase — so developers never built from the wrong one.',
  'Joined every grooming session to flag design constraints early and make scope trade-offs explicit.',
  'Documented every component in Storybook with its interaction states.',
  'Wrote the gear tooltip into the hand-off notes so discoverability survived the build.',
];

/** Attention Insight report: four desktop variants of the Settings → Partners page. */
export const botkeeperAttentionVariants = [
  { name: 'Variant 1', placement: 'Settings links in a left column', clarity: 57, cta: ['2.7%', '1%'] },
  { name: 'Variant 2', placement: 'Settings links as a row above the tabs', clarity: 57, cta: ['1.7%', '1.2%'] },
  { name: 'Variant 3', placement: 'Settings links top right', clarity: 61, cta: ['1.8%', '1.1%'] },
  { name: 'Variant 4', placement: 'Segmented toggle top right', clarity: 60, cta: ['1.9%', '1.6%', '1.1%'] },
];

export const botkeeperResults = [
  { value: '−34%', label: 'Time to edit', detail: '58 s → 38 s' },
  { value: '−27%', label: 'Permission errors reported to CS', detail: 'First month' },
  { value: '+1.3', label: 'CSAT on configuration tasks', detail: '3.2 → 4.5' },
  { value: '+18%', label: 'Feature adoption', detail: 'First month' },
];
